/**
 * /sites/:siteId/standards — the factory's brain, operator-editable.
 *
 * Three texts decide everything the writer produces for a site. Editing
 * them here is how the operator tunes voice and onboards a NEW site
 * without engineering: write the gold-standard article, define the
 * laws and the drawing style, then start generating.
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { api, ApiError } from '../lib/api.ts'

interface StandardRow {
  key: string
  value: string
  updated_at: number
}

const KEYS: { key: string; label: string; blurb: string; rows: number }[] = [
  {
    key: 'exemplar_article',
    label: 'Gold-standard article',
    blurb:
      'A complete article in your own voice. The writer imitates its structure, pacing, and honesty for every new keyword. The law: no exemplar, no generation.',
    rows: 16,
  },
  {
    key: 'prose_laws',
    label: 'Prose laws',
    blurb:
      'The rules every article must obey (what numbers are allowed, what the narrator may claim, banned punctuation, and so on).',
    rows: 10,
  },
  {
    key: 'scene_language',
    label: 'Drawing style',
    blurb:
      'The visual language for every illustration: canvas, palette, what documents and people look like, what is never drawn.',
    rows: 10,
  },
]

export function StandardsScreen() {
  const { siteId = 'll' } = useParams()
  const [values, setValues] = useState<Record<string, string>>({})
  const [updated, setUpdated] = useState<Record<string, number>>({})
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set())
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [msg, setMsg] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api<{ standards: StandardRow[] }>(`/api/sites/${siteId}/standards`)
      .then((res) => {
        const v: Record<string, string> = {}
        const u: Record<string, number> = {}
        for (const row of res.standards) {
          v[row.key] = row.value
          u[row.key] = row.updated_at
        }
        setValues(v)
        setUpdated(u)
        setDirtyKeys(new Set())
      })
      .catch((e) => setMsg(`Failed to load: ${(e as Error).message}`))
      .finally(() => setLoading(false))
  }, [siteId])

  async function save(key: string) {
    setSavingKey(key)
    setMsg('')
    try {
      await api(`/api/sites/${siteId}/standards/${key}`, {
        method: 'PUT',
        body: JSON.stringify({ value: values[key] ?? '' }),
      })
      setUpdated((prev) => ({ ...prev, [key]: Date.now() }))
      setDirtyKeys((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
      setMsg('Saved. Applies to every article generated from now on; existing articles are untouched.')
    } catch (e) {
      const detail =
        e instanceof ApiError && typeof (e.body as { detail?: string })?.detail === 'string'
          ? (e.body as { detail: string }).detail
          : (e as Error).message
      setMsg(`Save failed: ${detail}`)
    } finally {
      setSavingKey(null)
    }
  }

  if (loading) return <div className="loading">Loading standards…</div>

  return (
    <div className="screen">
      <h1>Standards</h1>
      <p className="muted">
        These three texts are the writer's brain for this site. Edit carefully:
        small changes, then test with one keyword before a batch. Changes only
        affect articles generated after you save.
      </p>
      {msg ? <p style={{ color: msg.startsWith('Save failed') ? 'crimson' : 'var(--text-muted)' }}>{msg}</p> : null}
      {KEYS.map(({ key, label, blurb, rows }) => (
        <div key={key} className="card" style={{ marginTop: 16, display: 'grid', gap: 8 }}>
          <div>
            <strong>{label}</strong>
            <div className="muted" style={{ fontSize: 13 }}>{blurb}</div>
          </div>
          <textarea
            rows={rows}
            style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
            value={values[key] ?? ''}
            placeholder={values[key] === undefined ? 'Not set yet for this site.' : undefined}
            onChange={(e) => {
              setValues((prev) => ({ ...prev, [key]: e.target.value }))
              setDirtyKeys((prev) => new Set(prev).add(key))
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 12 }}>
              {(values[key] ?? '').length.toLocaleString()} characters
              {updated[key] ? ` · last saved ${new Date(updated[key]).toLocaleString()}` : ''}
            </span>
            <button disabled={savingKey === key || !dirtyKeys.has(key)} onClick={() => save(key)}>
              {savingKey === key ? 'Saving…' : dirtyKeys.has(key) ? `Save ${label.toLowerCase()}` : 'Saved'}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
